/**
 * Visitor country detection — client-side via free public geo IP API,
 * cached in sessionStorage. Returns an uppercase ISO alpha-2 code or null.
 *
 * Tries Cloudflare's trace endpoint first (no CORS, very fast), then
 * country.is as a fallback. No API keys, no PII stored.
 */
import { useEffect, useState } from "react";

const CACHE_KEY = "rawan:visitor-country";

async function fetchCloudflare(): Promise<string | null> {
  try {
    const res = await fetch("https://www.cloudflare.com/cdn-cgi/trace", { cache: "no-store" });
    if (!res.ok) return null;
    const txt = await res.text();
    const m = txt.match(/^loc=([A-Z]{2})$/m);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

async function fetchCountryIs(): Promise<string | null> {
  try {
    const res = await fetch("https://api.country.is/", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { country?: string };
    return data?.country ? data.country.toUpperCase() : null;
  } catch {
    return null;
  }
}

export async function detectVisitorCountry(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  try {
    const cached = window.sessionStorage.getItem(CACHE_KEY);
    if (cached) return cached || null;
  } catch {
    /* ignore */
  }
  const code = (await fetchCloudflare()) ?? (await fetchCountryIs());
  try {
    window.sessionStorage.setItem(CACHE_KEY, code ?? "");
  } catch {
    /* ignore */
  }
  return code;
}

export function useVisitorCountry(): { country: string | null; loading: boolean } {
  const [country, setCountry] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    detectVisitorCountry().then((c) => {
      if (cancelled) return;
      setCountry(c);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return { country, loading };
}
